import React from 'react';

// Canonical spot-address pattern used across the app.
//
//     📍 **place_name** — address
//
// The place name is bold; the address is a clickable Google Maps link.
// Prefer `placeId` for the map URL (stable identifier); fall back to a
// text search on the address. By default the name is not a link — pass
// `nameHref` to make it one (e.g. link to /review/:id).
//
// Implemented with plain span/anchor elements (no MUI dependency) so it
// can be dropped inside a Joy <Typography>, a Material <Typography>, or
// a bare <div> without style leakage. The caller controls font size /
// color via the surrounding Typography or by passing `style`.
//
// Keep this component the single source of truth for address lines so
// future tweaks (icon, separator, link styling) show up everywhere.
//
// Props:
//   placeName     — string; shown bold before the dash.
//   placeAddress  — string; shown after the dash, wrapped in a Maps link.
//   placeId       — string; optional Google Places place_id for a stable
//                   Maps URL. If omitted we fall back to an address query.
//   nameHref      — string; if provided, the place name becomes a link
//                   to this URL (e.g. /review/:id). Leave undefined when
//                   the row is already inside an overlay Card link.
//   style         — inline style merged onto the outer span.
//   className     — extra class on the outer span.
const SpotAddressLine = ({
    placeName,
    placeAddress,
    placeId,
    nameHref,
    style,
    className,
}) => {
    if (!placeName && !placeAddress) return null;

    const mapsHref = placeId
        ? `https://www.google.com/maps/place/?q=place_id:${placeId}`
        : placeAddress
            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(placeAddress)}`
            : null;

    // position: relative + zIndex guards against surrounding overlay Card
    // links — we want address/name clicks to land on their own targets.
    return (
        <span
            className={className}
            style={{ position: 'relative', zIndex: 1, ...style }}
        >
            {'\uD83D\uDCCD '}
            {placeName && (
                nameHref ? (
                    <a
                        href={nameHref}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'inherit', fontWeight: 600, textDecoration: 'none' }}
                    >
                        {placeName}
                    </a>
                ) : (
                    <strong>{placeName}</strong>
                )
            )}
            {placeName && placeAddress && ' — '}
            {placeAddress && (
                mapsHref ? (
                    <a
                        href={mapsHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        // Stop propagation so the Maps link doesn't trigger
                        // surrounding overlay links (e.g. a Card that
                        // navigates to the review detail page).
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                        {placeAddress}
                    </a>
                ) : placeAddress
            )}
        </span>
    );
};

export default SpotAddressLine;
